import React, { useState, useMemo } from 'react';
import { SpreadsheetTable } from './SpreadsheetTable';
import { resolveSheets, buildGrid, colLabel } from '../utils/worksheetUtils';

interface WorksheetDiffViewerProps {
  oldData: Record<string, any> | null | undefined;
  newData: Record<string, any>;
}

export const WorksheetDiffViewer: React.FC<WorksheetDiffViewerProps> = ({ oldData, newData }) => {
  const newResolved = useMemo(() => resolveSheets(newData), [newData]);
  const oldResolved = useMemo(() => (oldData ? resolveSheets(oldData) : null), [oldData]);

  const sheetNames = useMemo(() => Object.keys(newResolved), [newResolved]);
  const [activeSheet, setActiveSheet] = useState(sheetNames[0] ?? '');

  const newCells = newResolved[activeSheet] ?? {};
  const oldCells = oldResolved?.[activeSheet] ?? {};

  const { headers, rows, rowClasses, cellClasses } = useMemo(() => {
    const grid = buildGrid(newCells);

    if (!oldData) {
      return { ...grid, cellClasses: undefined };
    }

    const diffClasses: (string | undefined)[][] = [];
    let maxCol = grid.headers.length;
    let maxRow = grid.rows.length;

    for (let r = 1; r <= maxRow; r++) {
      const rowDiff: (string | undefined)[] = [];
      for (let c = 1; c <= maxCol; c++) {
        const ref = `${colLabel(c)}${r}`;
        const newVal = newCells[ref];
        const oldVal = oldCells[ref];
        const newExists = ref in newCells;
        const oldExists = ref in oldCells;

        if (newExists && !oldExists) {
          rowDiff.push('ss-cell-added');
        } else if (newExists && oldExists && String(newVal ?? '') !== String(oldVal ?? '')) {
          rowDiff.push('ss-cell-changed');
        } else {
          rowDiff.push(undefined);
        }
      }
      diffClasses.push(rowDiff);
    }

    return { ...grid, cellClasses: diffClasses };
  }, [newCells, oldCells, oldData]);

  if (sheetNames.length === 0) {
    return <div className="ss-empty">No worksheet data available.</div>;
  }

  return (
    <div className="ws-diff-outer">
      {sheetNames.length > 1 && (
        <div className="ws-sheet-tabs">
          {sheetNames.map(name => (
            <button
              key={name}
              className={`ws-sheet-tab ${activeSheet === name ? 'active' : ''}`}
              onClick={() => setActiveSheet(name)}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      <SpreadsheetTable
        headers={headers}
        rows={rows}
        rowClasses={rowClasses}
        cellClasses={cellClasses}
        maxHeight={400}
      />
    </div>
  );
};
